import { useState, useEffect, useMemo } from 'react';
import { Gift, CheckCircle2, Clock, AlertCircle, X, Loader2, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useMe } from '../hooks/useProfile';
import { usePendingSummary } from '../hooks/usePendingPoints';
import NavShell from '../components/NavShell';
import { supabase } from '../lib/supabase';
import { advancePendingPoints } from '../lib/api';
import { API_BASE } from '../lib/apiBase';
import { daysUntil, getAvailablePoints, pointsToUsd } from '../lib/points';

async function authHeaders() {
  const { data } = await supabase.auth.getSession();
  return { Authorization: `Bearer ${data.session?.access_token}`, 'Content-Type': 'application/json' };
}

export type RedeemResult = { type: 'success' | 'review' | 'processing' | 'error'; link?: string; message?: string };

/**
 * Map the POST /api/redemptions response to the member-facing result state.
 * Post-consolidation the create lands `pending` for everyone except L5
 * auto-release (which fulfills in-request, returning a redemptionLink). The old
 * code keyed on `redemptionLink` and so showed "processing" for every pending
 * redemption — wrong. Key on `status`:
 *   fulfilled → success (link present for L5); failed → error (points refunded);
 *   processing → processing (L5 async VI); pending|held → in review.
 */
export function redeemResultFromResponse(
  ok: boolean,
  data: { success?: boolean; status?: string; redemptionLink?: string; error?: string },
): RedeemResult {
  if (!ok || !data.success) return { type: 'error', message: data.error ?? 'Something went wrong' };
  if (data.status === 'fulfilled') return { type: 'success', link: data.redemptionLink };
  if (data.status === 'failed') return { type: 'error', message: 'Your points have been returned to your account' };
  if (data.status === 'processing') return { type: 'processing' };
  return { type: 'review' }; // pending | held — the common case
}

/**
 * On a successful redemption submit the create already DEBITED the points. Reflect
 * that in the cache IMMEDIATELY so the member can't re-attempt a redemption they
 * just spent the points on:
 *   1. OPTIMISTICALLY drop the cached ['me'].points_balance by the cost — the
 *      available badge + affordability checks recompute instantly.
 *   2. Invalidate ['me'] (reconcile to the server's post-debit balance),
 *      ['pending-summary'] and ['redemptions'] (new row in history).
 * The optimistic value and the refetched value agree (both = balance − cost), so
 * there's no flicker.
 */
export function applyRedemptionSuccessToCache(queryClient: QueryClient, pointsCost: number): void {
  queryClient.setQueryData(['me'], (old: unknown) => {
    if (!old || typeof old !== 'object') return old;
    const m = old as { points_balance?: number };
    return { ...m, points_balance: Math.max(0, (m.points_balance ?? 0) - pointsCost) };
  });
  void queryClient.invalidateQueries({ queryKey: ['me'] });
  void queryClient.invalidateQueries({ queryKey: ['pending-summary'] });
  void queryClient.invalidateQueries({ queryKey: ['redemptions'] });
}

interface Brand {
  sku: string; name: string; currency: string;
  min_amount: number | null; max_amount: number | null;
  fixed_amounts: number[]; faceplate_url: string | null;
  is_featured: boolean; display_order: number; category: string;
  allowed_amounts: number[];
}

interface RedemptionItem {
  id: string; created_at: string; amount_usd: number | null; points_cost: number;
  vi_sku: string | null; brand_name: string | null; status: string;
  has_link: boolean; provider: string;
}

// PayPal disabled until PayPal Payouts API is implemented.
// VI mock provider is not a real payout. Re-enable when live.
const ENABLED_REDEMPTION_TYPES = ['gift_card'] as const;

// Brand SKUs that should be hidden from the wall while their payout path
// isn't production-ready. Matches on SKU (exact) and on a name substring.
const HIDDEN_BRAND_SKUS = new Set(['TEST-PAYPAL']);
const HIDDEN_BRAND_NAME_SUBSTRINGS = ['paypal'];

function isBrandEnabled(brand: { sku: string; name: string }): boolean {
  if (HIDDEN_BRAND_SKUS.has(brand.sku)) return false;
  const lower = brand.name.toLowerCase();
  return !HIDDEN_BRAND_NAME_SUBSTRINGS.some((s) => lower.includes(s));
}

const BRAND_COLORS: Record<string, string> = {
  amazon: 'from-gray-800 to-gray-900', visa: 'from-blue-800 to-blue-900',
  target: 'from-red-600 to-red-700', starbucks: 'from-green-700 to-green-800',
  paypal: 'from-blue-600 to-blue-700', default: 'from-purple-600 to-purple-700',
};

function getBrandColor(name: string): string {
  const lower = name.toLowerCase();
  for (const [key, val] of Object.entries(BRAND_COLORS)) {
    if (lower.includes(key)) return val;
  }
  return BRAND_COLORS.default;
}

function getDenominations(brand: Brand): number[] {
  if (brand.allowed_amounts.length > 0) return brand.allowed_amounts;
  if (brand.fixed_amounts.length > 0) return brand.fixed_amounts;
  if (brand.min_amount != null && brand.max_amount != null) {
    return [5, 10, 15, 20, 25, 50, 75, 100].filter((v) => v >= brand.min_amount! && v <= brand.max_amount!);
  }
  return [5, 10, 25];
}

function getMinDenom(brand: Brand): number {
  const denoms = getDenominations(brand);
  return denoms.length > 0 ? Math.min(...denoms) : 5;
}

const CATEGORIES = ['All', 'Retail', 'Restaurants', 'Prepaid', 'Popular'] as const;

// ── Brand Card ──────────────────────────────────────────────────────────────
function BrandCard({ brand, affordable, onClick, featured }: { brand: Brand; affordable: boolean; onClick: () => void; featured?: boolean }) {
  const denoms = getDenominations(brand);
  const denomText = brand.min_amount != null && brand.max_amount != null
    ? `$${brand.min_amount} – $${brand.max_amount}`
    : denoms.map((d) => `$${d}`).join(', ');
  const minPts = getMinDenom(brand) * 100;

  return (
    <button
      onClick={affordable ? onClick : undefined}
      className={`bg-white rounded-2xl shadow-sm overflow-hidden text-left transition-all ${affordable ? 'hover:shadow-md cursor-pointer' : 'opacity-60 cursor-default'}`}
    >
      <div className={`${featured ? 'aspect-video' : 'aspect-[16/10]'} bg-gradient-to-br ${getBrandColor(brand.name)} flex items-center justify-center`}>
        {brand.faceplate_url ? (
          <img src={brand.faceplate_url} alt={brand.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-white text-2xl font-bold">{brand.name.charAt(0)}</span>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-bold text-[#111827] truncate">{brand.name}</p>
        <p className="text-xs text-[#6B7280]">{denomText}</p>
        {!affordable && <p className="text-xs text-[#EF4444] mt-1">Need {minPts} pts</p>}
      </div>
    </button>
  );
}

// ── Denomination Sheet ──────────────────────────────────────────────────────
export function DenomSheet({ brand, balance, onClose, onConfirm, confirming, result }: {
  brand: Brand; balance: number; onClose: () => void;
  onConfirm: (amount: number) => void; confirming: boolean;
  result: { type: 'success' | 'review' | 'processing' | 'error'; link?: string; message?: string } | null;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const denoms = getDenominations(brand);
  const { data: me } = useMe();

  if (result) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
        <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 text-center space-y-4" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 text-[#6B7280]"><X className="w-5 h-5" /></button>
          {result.type === 'success' && (
            <>
              <CheckCircle2 className="w-16 h-16 text-[#10B981] mx-auto" />
              <h3 className="text-xl font-bold text-[#111827]">Your gift card is ready!</h3>
              {result.link && <a href={result.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#10B981] text-white font-medium px-6 py-3 rounded-full hover:bg-[#059669]"><ExternalLink className="w-4 h-4" />Claim your {brand.name} gift card</a>}
              <p className="text-xs text-[#6B7280]">We've also emailed this link to {me?.member?.email}</p>
              <p className="text-xs text-[#9CA3AF]">This link can only be used once — save it somewhere safe</p>
            </>
          )}
          {result.type === 'review' && (
            <>
              <Clock className="w-16 h-16 text-[#5B4FE8] mx-auto" />
              <h3 className="text-xl font-bold text-[#111827]">Your redemption is being reviewed</h3>
              <p className="text-sm text-[#6B7280]">We'll notify {me?.member?.email} once it's approved — usually quick. Your points have already been deducted and will be returned if it can't be fulfilled.</p>
              <p className="text-xs text-[#9CA3AF]">You can track its status in your redemption history below.</p>
            </>
          )}
          {result.type === 'processing' && (
            <>
              <Clock className="w-16 h-16 text-[#F97316] mx-auto" />
              <h3 className="text-xl font-bold text-[#111827]">Your reward is being processed</h3>
              <p className="text-sm text-[#6B7280]">We'll email your gift card link to {me?.member?.email} as soon as it's ready.</p>
            </>
          )}
          {result.type === 'error' && (
            <>
              <AlertCircle className="w-16 h-16 text-[#EF4444] mx-auto" />
              <h3 className="text-xl font-bold text-[#111827]">Something went wrong</h3>
              <p className="text-sm text-[#6B7280]">{result.message ?? 'Your points have been returned to your account'}</p>
            </>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 space-y-4 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} disabled={confirming} className="absolute top-4 right-4 text-[#6B7280] disabled:opacity-30"><X className="w-5 h-5" /></button>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getBrandColor(brand.name)} flex items-center justify-center`}>
            <span className="text-white text-lg font-bold">{brand.name.charAt(0)}</span>
          </div>
          <div>
            <p className="font-bold text-[#111827]">{brand.name}</p>
            <p className="text-xs text-[#6B7280]">Select amount</p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {denoms.map((d) => {
            const pts = d * 100;
            const canAfford = balance >= pts;
            return (
              <button key={d} onClick={() => canAfford && setSelected(d)} disabled={!canAfford}
                className={`py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                  selected === d ? 'bg-[#10B981] text-white border-[#10B981]' : canAfford ? 'border-[#E5E7EB] text-[#111827] hover:border-[#10B981]' : 'border-[#E5E7EB] text-[#D1D5DB] cursor-not-allowed'
                }`}
              >${d}</button>
            );
          })}
        </div>

        <p className="text-xs text-[#6B7280] text-center">Your balance: {balance.toLocaleString()} pts available</p>

        <button
          onClick={() => selected && onConfirm(selected)}
          disabled={!selected || confirming || (!!selected && balance < selected * 100)}
          className={`w-full py-3 rounded-full font-medium text-sm flex items-center justify-center gap-2 ${
            selected && balance >= selected * 100 ? 'bg-[#10B981] text-white hover:bg-[#059669]' : 'bg-gray-200 text-[#9CA3AF]'
          } disabled:opacity-60`}
        >
          {confirming ? <><Loader2 className="w-4 h-4 animate-spin" />Processing your reward...</> : selected ? `Redeem $${selected} · ${selected * 100} pts` : 'Select an amount'}
        </button>
      </motion.div>
    </div>
  );
}

// ── Redemption Threshold Note ───────────────────────────────────────────────
function RedemptionThresholdNote() {
  const { data: me } = useMe();
  const trustLevel = (me as { trust_level?: number } | undefined)?.trust_level ?? 1;
  // Member's CURRENT minimum (economics unchanged — founder decision 2026-06-24).
  // We make the path explicit by showing exact points remaining to it, and
  // surface $5 as the next unlock for L1 members.
  const minPts = (me as { redemption_minimum_points?: number } | undefined)?.redemption_minimum_points
    ?? (trustLevel >= 2 ? 500 : 1000);
  const usd = pointsToUsd(minPts);
  const available = getAvailablePoints(me);
  const toGo = Math.max(0, minPts - available);
  return (
    <div className="bg-white rounded-2xl shadow-sm p-3 px-4 text-xs text-[#374151] flex items-start gap-2">
      <Gift className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" />
      <p>
        {toGo > 0 ? (
          <>
            <span className="font-bold text-[#10B981] tabular-nums">{toGo.toLocaleString()} pts</span> to your first reward —
            cash out at <span className="font-bold text-[#111827] tabular-nums">{minPts.toLocaleString()} pts (${usd})</span>.
          </>
        ) : (
          <>You can cash out now — minimum <span className="font-bold text-[#111827] tabular-nums">{minPts.toLocaleString()} pts (${usd})</span>.</>
        )}
        {trustLevel < 2 && ' Reach Trust Level 2 to unlock the $5.00 minimum.'}
      </p>
    </div>
  );
}

// ── Pending Points Breakdown ────────────────────────────────────────────────
export function PendingBreakdown() {
  const { data } = usePendingSummary();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState<
    | { kind: 'one'; ledgerId: string; advance: number; forfeit: number }
    | { kind: 'all'; advance: number; forfeit: number }
    | null
  >(null);
  const [busyKey, setBusyKey] = useState<string | null>(null); // 'all' or ledger id
  const [advanceError, setAdvanceError] = useState<string | null>(null);

  const trustLevel = (me as { trust_level?: number } | undefined)?.trust_level ?? 1;
  const unlocked = trustLevel >= 3;
  // Full instant-credit gate (L4+ AND fraud_tier ∉ {shadow,banned}), computed
  // server-side. When true, NEW earnings credit instantly and these pending
  // points are older ones still clearing — so we say so, and only to them.
  const instantEligible = (me as { instant_credit_eligible?: boolean } | undefined)?.instant_credit_eligible ?? false;

  if (!data || data.totalPending === 0) return null;

  async function runAdvance(ledgerIds?: string[]) {
    const key = ledgerIds && ledgerIds.length === 1 ? ledgerIds[0] : 'all';
    setBusyKey(key);
    setAdvanceError(null);
    try {
      await advancePendingPoints(ledgerIds);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pending-summary'] }),
        queryClient.invalidateQueries({ queryKey: ['me'] }),
      ]);
      setConfirm(null);
    } catch (err) {
      // Money-touching: surface a retryable error instead of failing silently,
      // and report to monitoring. The modal stays open so the member can retry.
      Sentry.captureException(err);
      setAdvanceError("We couldn't advance your points. Please try again.");
    } finally {
      setBusyKey(null);
    }
  }

  const totalForfeitAll = data.totalPending - data.totalAdvanceable;
  // Soonest batch release for the countdown shown to members who can't advance.
  const firstBatchDays = data.entries.length > 0
    ? Math.min(...data.entries.map((e) => daysUntil(e.pendingUntil)))
    : null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-[#111827]">Pending points</h3>
          {/* Honest reframe: it's ~30 days, not "briefly" — framed positively. */}
          <p className="text-xs text-[#6B7280] mt-0.5">
            These points clear over ~30 days as buyers confirm each completion — it protects
            everyone from fraud.
          </p>
          {instantEligible && (
            <p className="text-xs text-[#059669] mt-1">
              As a trusted member, your <span className="font-medium">new</span> earnings now
              credit instantly — these are older points still clearing.
            </p>
          )}
          {unlocked ? (
            <p className="text-sm font-medium text-[#10B981] mt-2 tabular-nums">
              {data.totalAdvanceable.toLocaleString()} pts advanceable now
              <span className="text-[#6B7280] font-normal"> of {data.totalPending.toLocaleString()} pending</span>
            </p>
          ) : (
            <p className="text-sm font-medium text-[#111827] mt-2 tabular-nums">
              {data.totalPending.toLocaleString()} pts pending
              {firstBatchDays !== null && (
                <span className="text-[#6B7280] font-normal">
                  {' '}&middot; first batch in {firstBatchDays} {firstBatchDays === 1 ? 'day' : 'days'}
                </span>
              )}
            </p>
          )}
          {/* Honest hedge framing — advancing trades part of the lot for cash now
              (40%→70% of it, by age); the rest is forfeited. Not encouraged, not alarmed. */}
          {unlocked && data.totalAdvanceable > 0 && (
            <p className="text-xs text-[#6B7280] mt-2">
              Advancing early trades some of these points for cash now — you'd get{' '}
              <span className="font-medium text-[#111827] tabular-nums">{data.totalAdvanceable.toLocaleString()}</span>{' '}
              and give up{' '}
              <span className="font-medium text-[#EF4444] tabular-nums">{totalForfeitAll.toLocaleString()}</span>.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Advancement is a Trust L3 benefit — only show the control to
              members who can actually use it; never a locked/disabled button.
              It's a fair paid hedge (40%→70% by age), so a normal neutral
              secondary button — not a green CTA, not a footgun-quiet link. */}
          {unlocked && (
            <button
              onClick={() => data.totalAdvanceable > 0 && setConfirm({ kind: 'all', advance: data.totalAdvanceable, forfeit: totalForfeitAll })}
              disabled={data.totalAdvanceable === 0 || busyKey !== null}
              className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap border ${
                data.totalAdvanceable > 0
                  ? 'border-[#D1D5DB] text-[#374151] hover:bg-gray-50'
                  : 'border-gray-100 text-[#9CA3AF] cursor-not-allowed'
              }`}
            >
              Advance all
            </button>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-[#5B4FE8] font-medium hover:underline whitespace-nowrap"
          >
            {expanded ? 'Hide' : 'View'} breakdown
          </button>
        </div>
      </div>
      {!unlocked && (
        <p className="text-xs text-[#6B7280] -mt-1">
          Reach Trust Level 3 for early access to pending points.
        </p>
      )}
      {expanded && (
        <div className="space-y-2 pt-2 border-t border-gray-100">
          {data.entries.map((e) => {
            const day = Math.min(30, Math.floor(e.daysSinceEarned));
            const pct = Math.min(100, (e.daysSinceEarned / 30) * 100);
            const forfeit = e.points - e.advanceablePoints;
            const canAdvanceRow = unlocked && e.advanceablePoints > 0;
            const releaseDays = daysUntil(e.pendingUntil);
            return (
              <div key={e.id} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs gap-2">
                  <span className="text-[#111827] font-medium tabular-nums">
                    {e.points.toLocaleString()} pts
                  </span>
                  {unlocked ? (
                    <>
                      <span className="text-[#6B7280] tabular-nums">
                        Day {day} of 30 — {e.advanceablePercent.toFixed(1)}% unlocked
                        {' · '}
                        <span className="text-[#10B981] font-medium">{e.advanceablePoints.toLocaleString()} pts</span>
                      </span>
                      <button
                        onClick={() => canAdvanceRow && setConfirm({ kind: 'one', ledgerId: e.id, advance: e.advanceablePoints, forfeit })}
                        disabled={!canAdvanceRow || busyKey !== null}
                        className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${
                          canAdvanceRow
                            ? 'bg-[#E6F7EE] text-[#059669] hover:bg-[#D1F0DE]'
                            : 'bg-gray-100 text-[#9CA3AF] cursor-not-allowed'
                        }`}
                      >
                        Advance now
                      </button>
                    </>
                  ) : (
                    <span className="text-[#6B7280] tabular-nums">
                      {releaseDays === 0 ? 'Releasing today' : `Releases in ${releaseDays} ${releaseDays === 1 ? 'day' : 'days'}`}
                    </span>
                  )}
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#10B981] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={() => busyKey === null && (setConfirm(null), setAdvanceError(null))}>
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 space-y-4 relative" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#111827]">Advance pending points?</h3>
            <p className="text-sm text-[#374151]">
              You'll receive <span className="font-bold text-[#10B981] tabular-nums">{confirm.advance.toLocaleString()} pts</span> now and forfeit{' '}
              <span className="font-bold text-[#EF4444] tabular-nums">{confirm.forfeit.toLocaleString()} pts</span>.
            </p>
            <p className="text-xs text-[#6B7280]">This cannot be undone.</p>
            {advanceError && (
              <p className="text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{advanceError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => busyKey === null && (setConfirm(null), setAdvanceError(null))}
                disabled={busyKey !== null}
                className="flex-1 py-2.5 rounded-full text-sm font-medium border border-[#E5E7EB] text-[#374151] hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => void runAdvance(confirm.kind === 'one' ? [confirm.ledgerId] : undefined)}
                disabled={busyKey !== null}
                className="flex-1 py-2.5 rounded-full text-sm font-medium bg-[#10B981] text-white hover:bg-[#059669] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {busyKey !== null && <Loader2 className="w-4 h-4 animate-spin" />}
                {advanceError ? 'Try again' : 'Confirm advance'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ── Redemption History ──────────────────────────────────────────────────────
function RedemptionHistory() {
  const [items, setItems] = useState<RedemptionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [linkErrorId, setLinkErrorId] = useState<string | null>(null);
  const [linkBusyId, setLinkBusyId] = useState<string | null>(null);

  async function fetchHistory(limit?: string) {
    setLoading(true);
    setLoadError(false);
    try {
      const h = await authHeaders();
      const url = limit ? `${API_BASE}/api/redemptions?limit=${limit}` : `${API_BASE}/api/redemptions`;
      const res = await fetch(url, { headers: h });
      if (!res.ok) throw new Error(`redemption history ${res.status}`);
      const data = await res.json();
      setItems(data.redemptions ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      Sentry.captureException(err);
      setLoadError(true);
    }
    setLoading(false);
  }

  useEffect(() => { void fetchHistory(); }, []);

  async function handleViewLink(id: string) {
    setLinkBusyId(id);
    setLinkErrorId(null);
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE}/api/redemptions/${id}/link`, { headers: h });
      if (!res.ok) throw new Error(`redemption link ${res.status}`);
      const data = await res.json();
      if (data.redemptionLink) window.open(data.redemptionLink, '_blank');
      else throw new Error('redemption link missing');
    } catch (err) {
      // Money-touching: this is the member's actual gift-card link.
      Sentry.captureException(err);
      setLinkErrorId(id);
    }
    setLinkBusyId(null);
  }

  function handleShowAll() {
    setShowAll(true);
    void fetchHistory('all');
  }

  if (loading) return null;
  if (loadError && items.length === 0) {
    return (
      <div className="text-center py-8 space-y-2">
        <p className="text-sm text-[#6B7280]">We couldn't load your redemptions.</p>
        <button onClick={() => void fetchHistory(showAll ? 'all' : undefined)} className="text-sm text-[#5B4FE8] font-medium hover:underline">Try again</button>
      </div>
    );
  }
  if (items.length === 0) return <p className="text-sm text-[#6B7280] text-center py-8">No redemptions yet — redeem your first reward above</p>;

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold text-[#111827]">Recent redemptions</h3>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-[#6B7280] border-b border-gray-100">
            <th className="px-4 py-2">Brand</th><th className="px-4 py-2">Amount</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Status</th><th className="px-4 py-2"></th>
          </tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 last:border-0">
                <td className="px-4 py-3 text-[#111827] font-medium">{r.brand_name ?? r.vi_sku ?? 'Gift Card'}</td>
                <td className="px-4 py-3 text-[#111827]">${(r.amount_usd ?? 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-[#6B7280]">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    r.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                    r.status === 'processing' || r.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>{r.status === 'fulfilled' ? 'completed' : r.status}</span>
                </td>
                <td className="px-4 py-3">
                  {r.status === 'fulfilled' && r.has_link && (
                    <button
                      onClick={() => void handleViewLink(r.id)}
                      disabled={linkBusyId === r.id}
                      className="text-xs font-medium hover:underline disabled:opacity-50 text-[#5B4FE8]"
                    >
                      {linkBusyId === r.id ? 'Opening…' : linkErrorId === r.id ? 'Couldn’t open — retry' : 'View link'}
                    </button>
                  )}
                  {r.status === 'failed' && <span className="text-xs text-[#6B7280]">Points returned</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!showAll && total > 5 && (
        <p className="text-center text-sm text-[#6B7280]">
          Showing 5 of {total} · <button onClick={handleShowAll} className="text-[#5B4FE8] font-medium hover:underline">Show all</button>
        </p>
      )}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function RedemptionPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState<string>('All');
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'review' | 'processing' | 'error'; link?: string; message?: string } | null>(null);

  const { data: pendingSummary } = usePendingSummary();
  const totalPoints = me?.points_balance ?? 0;
  const pendingPoints = pendingSummary?.totalPending ?? ((me as Record<string, unknown>)?.points_pending as number ?? 0);
  const advanceablePoints = pendingSummary?.totalAdvanceable ?? 0;
  const confirmedPoints = totalPoints - pendingPoints;
  // Available badge shows confirmed points only. Advanceable pending is
  // accessed via the dedicated "Advance now" / "Advance all" actions in
  // the PendingBreakdown card below — once advanced, those points convert
  // to confirmed and roll into this balance. Available + Pending then sums
  // to member.total_points (= the nav header).
  const balance = confirmedPoints;

  useEffect(() => {
    (async () => {
      try {
        const h = await authHeaders();
        const res = await fetch(`${API_BASE}/api/vi/brands`, { headers: h });
        if (res.ok) {
          const data = await res.json();
          // Filter out brands whose payout path isn't production-ready (see
          // ENABLED_REDEMPTION_TYPES / isBrandEnabled at top of file).
          const all = (data.brands ?? []) as Brand[];
          setBrands(all.filter(isBrandEnabled));
        }
      } catch (err) {
        // Catalog load failure already surfaces "temporarily unavailable"
        // below; report it so it isn't invisible to monitoring.
        Sentry.captureException(err);
      }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (category === 'All') return brands;
    if (category === 'Popular') return brands.filter((b) => b.is_featured);
    return brands.filter((b) => b.category.toLowerCase() === category.toLowerCase());
  }, [brands, category]);

  const featured = useMemo(() => brands.filter((b) => b.is_featured), [brands]);

  async function handleConfirm(amount: number) {
    if (!selectedBrand) return;
    setConfirming(true);
    try {
      const h = await authHeaders();
      const res = await fetch(`${API_BASE}/api/redemptions`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ sku: selectedBrand.sku, amount }),
      });
      const data = await res.json();
      // GA4 redemption_initiated fires server-side from the /redemptions handler.
      setResult(redeemResultFromResponse(res.ok, data));
      if (res.ok && data.success) {
        // Points were debited at create — optimistically drop the balance NOW and
        // reconcile via refetch, so the member can't re-spend the just-spent points.
        applyRedemptionSuccessToCache(queryClient, amount * 100);
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again.' });
    }
    setConfirming(false);
  }

  return (
    <NavShell>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="px-4 sm:px-6 py-6 space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#1a7340] to-[#10B981] p-6 sm:p-8 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-display">Redeem points</h1>
              <p className="text-green-200 mt-1 text-sm">Choose from gift cards and prepaid rewards</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5">
                <span className="text-xl font-bold tabular-nums">{balance.toLocaleString()}</span>
                <span className="text-green-200 text-sm ml-1">Available</span>
              </div>
              {pendingPoints > 0 && (
                <div className="bg-white/15 backdrop-blur-sm rounded-full px-4 py-2.5" title={`${advanceablePoints.toLocaleString()} pts advanceable now`}>
                  <span className="text-lg font-bold tabular-nums">{pendingPoints.toLocaleString()}</span>
                  <span className="text-green-200 text-sm ml-1">Pending</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trust-tiered redemption minimum */}
        <RedemptionThresholdNote />

        {/* Pending points breakdown */}
        <PendingBreakdown />

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                category === c ? 'bg-[#10B981] text-white' : 'bg-white text-[#6B7280] border border-[#E5E7EB] hover:border-[#10B981]'
              }`}
            >{c}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#6B7280]">Loading brands...</div>
        ) : brands.length === 0 ? (
          <div className="text-center py-12 text-[#6B7280]">Redemption temporarily unavailable</div>
        ) : (
          <>
            {/* Featured */}
            {category === 'All' && featured.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-[#111827] mb-3">Featured</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {featured.map((b) => (
                    <BrandCard key={b.sku} brand={b} featured affordable={balance >= getMinDenom(b) * 100} onClick={() => { setSelectedBrand(b); setResult(null); }} />
                  ))}
                </div>
              </div>
            )}

            {/* Main grid */}
            <div>
              {category === 'All' && featured.length > 0 && <h2 className="text-lg font-bold text-[#111827] mb-3">More options</h2>}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {filtered.filter((b) => category !== 'All' || !b.is_featured).map((b) => (
                  <BrandCard key={b.sku} brand={b} affordable={balance >= getMinDenom(b) * 100} onClick={() => { setSelectedBrand(b); setResult(null); }} />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Redemption History */}
        <RedemptionHistory />
      </motion.div>

      {/* Denomination Sheet */}
      <AnimatePresence>
        {selectedBrand && (
          <DenomSheet brand={selectedBrand} balance={balance} onClose={() => { if (!confirming) { setSelectedBrand(null); setResult(null); } }} onConfirm={handleConfirm} confirming={confirming} result={result} />
        )}
      </AnimatePresence>
    </NavShell>
  );
}
